import { useCallback, useState, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'

import * as cartRepository from 'scripts/api/cart'
import { tracker } from 'scripts/react-components/utils/app'

import { Quantity } from 'checkout/components/Quantity'
import * as dataLayer from 'checkout/services/data-layer'

const Container = ({
  id,
  productId,
  name,
  price,
  brand,
  categoryTree,
  shopName,
  type,
  packaging,
  packagingCount,
  skuId,
  storeId,
  updateProduct,
  onError,
  onSuccess,
  fromCart,
  regionId
}) => {
  const [currentPackaging, setPackaging] = useState(packagingCount)
  const [productMaxStock, setProductMaxStock] = useState(Infinity)
  const [isReadyToUpdate, setIsReadyToUpdate] = useState(false)
  const [isProductRemoved, setIsProductRemoved] = useState(undefined)
  const [deltaValue, setDelta] = useState(0)

  const useFormatQuantity = useMemo(() => {
    return (
      Math.round((packaging * currentPackaging + Number.EPSILON) * 100) / 100
    )
  }, [currentPackaging, packaging])

  const handleInputChange = useCallback(
    (value) => {
      try {
        if (value && value > 0) {
          const delta =
            currentPackaging > value
              ? currentPackaging - value
              : value - currentPackaging
          setIsReadyToUpdate(true)
          setDelta(delta)
          setPackaging(value)

          dataLayerTrigger()
        }
      } catch (e) {
        setPackaging((p) => p)
      }

      dataLayerTrigger()
    },
    [currentPackaging, dataLayerTrigger]
  )

  const updateQuantity = useCallback(
    ({ value, isDecreasing = false }) => {
      const newPackaging =
        (parseInt(currentPackaging * 1000) + parseInt(value * 1000)) / 1000
      setDelta((d) => d + value)

      if (isDecreasing && newPackaging > productMaxStock) {
        setPackaging(newPackaging)
      }

      if (newPackaging > 0 && newPackaging <= productMaxStock) {
        setPackaging(newPackaging)
      }

      setIsReadyToUpdate(true)
      setIsProductRemoved(isDecreasing)
    },
    [currentPackaging, productMaxStock]
  )

  const dataLayerTrigger = useCallback(
    (total = null) => {
      const calculatePrice = (packaging, price) => {
        const finalPrice = packaging * price

        return finalPrice.toFixed(2)
      }

      const shipmentType = type.includes('pickupInStore')
        ? 'pickup-in-store'
        : 'delivery'

      const wishlistDataForTracker = {
        cart: {
          value: total,
          shipping_tier: shipmentType
        },
        product: {
          item_id: productId || id,
          item_name: name,
          discount: price?.discount ?? 0,
          item_brand: brand,
          quantity: currentPackaging,
          category: categoryTree,
          price: calculatePrice(packaging, price?.to),
          currency: 'BRL',
          affiliation:
            shopName === 'Leroy Merlin'
              ? 'delivery: Leroy Merlin'
              : `pickup-in-store: ${shopName}`
        }
      }

      if (deltaValue === 0) return

      if (deltaValue > 0) {
        tracker.addToCart(wishlistDataForTracker)

        dataLayer.addToCart({
          category: categoryTree,
          price: calculatePrice(packaging, price?.to),
          productBrand: brand,
          productId,
          productName: name,
          quantity: currentPackaging,
          regionId,
          shipmentType: shipmentType,
          storeId: storeId || '',
          marketPlace: shopName || ''
        })
      } else {
        tracker.removeFromCart(wishlistDataForTracker)

        dataLayer.removeFromCart({
          category: categoryTree,
          price: calculatePrice(packaging, price?.to),
          productBrand: brand,
          productId,
          productName: name,
          quantity: deltaValue * -1,
          regionId
        })
      }
    },
    [
      deltaValue,
      categoryTree,
      type,
      shopName,
      packaging,
      price,
      currentPackaging,
      brand,
      productId,
      name,
      regionId,
      storeId,
      id
    ]
  )

  useEffect(() => {
    setIsReadyToUpdate(true)

    return () => {
      setIsReadyToUpdate(false)
    }
  }, [])

  useEffect(() => {
    let updateCart

    if (isReadyToUpdate && packagingCount !== currentPackaging) {
      updateCart = setTimeout(async () => {
        await cartRepository
          .putProductsInCart({
            type,
            storeId: storeId || null,
            products: [
              {
                id,
                skuId: skuId || null,
                quantity: currentPackaging
              }
            ]
          })
          .then((response) => {
            const { data } = response

            try {
              dataLayerTrigger(data.total)
            } catch (err) {
              console.error(err)
            }

            updateProduct({ currentId: id, ...data })
            onSuccess({ data, currentPackaging, productMaxStock })
          })
          .catch(({ response: { data } }) => {
            const maxStock = data?.stock.find(
              (product) => product.id === id
            )?.max
            if (maxStock) {
              setPackaging(maxStock)
              setProductMaxStock(maxStock)
            }

            if (data?.errors) {
              onError(data, deltaValue)
            }
          })
          .finally(() => {
            setDelta(0)
          })
      }, 500)
    }

    return () => {
      clearTimeout(updateCart)
    }
  }, [
    currentPackaging,
    type,
    onError,
    onSuccess,
    productMaxStock,
    packagingCount,
    skuId,
    storeId,
    updateProduct,
    dataLayerTrigger,
    id,
    isReadyToUpdate,
    deltaValue
  ])

  useEffect(() => {
    setIsReadyToUpdate(false)
    setPackaging(packagingCount)
  }, [packagingCount])

  return (
    <Quantity
      fromCart={fromCart}
      useFormatQuantity={useFormatQuantity}
      packaging={packaging}
      updateQuantity={updateQuantity}
      handleInputChange={handleInputChange}
      packagingCount={packagingCount}
      productMaxStock={productMaxStock}
    />
  )
}

Container.propTypes = {
  id: PropTypes.string,
  quantity: PropTypes.number,
  packaging: PropTypes.number,
  skuId: PropTypes.string,
  storeId: PropTypes.string,
  type: PropTypes.string,
  updateProduct: PropTypes.func
}

Container.defaultProps = {
  onError: () => {},
  onSuccess: () => {}
}

export default Container
